import {
  SUPPORT_ROLE_CATEGORIES,
  SupportRequestCategory,
  type SupportRequestRole,
} from "Components/Support/SupportRequestCategory";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const SUPPORT_REQUEST_SUBJECT_MAX_LENGTH = 100;
export const SUPPORT_REQUEST_NAME_MAX_LENGTH = 100;
export const SUPPORT_REQUEST_DESCRIPTION_MIN_LENGTH = 10;
export const SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH = 2000;
export const SUPPORT_ATTACHMENT_MAX_SIZE_MB = 5;
export const SUPPORT_ATTACHMENT_ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
];

const RESOURCES = Resources.Support.Request.Validation;

const attachmentSchema = (t: TFunction) =>
  z
    .instanceof(File)
    .refine(
      (file) => file.size <= SUPPORT_ATTACHMENT_MAX_SIZE_MB * 1024 * 1024,
      {
        message: t(RESOURCES.AttachmentTooLarge, {
          maxSizeMb: SUPPORT_ATTACHMENT_MAX_SIZE_MB,
        }),
      },
    )
    .refine((file) => SUPPORT_ATTACHMENT_ACCEPTED_TYPES.includes(file.type), {
      message: t(RESOURCES.AttachmentTypeNotSupported),
    })
    .nullable();

/**
 * `isAnonymous` only toggles whether the reporter fields are required — they stay
 * on the type either way, so the form data shape doesn't change between sessions.
 * For a signed-in user the reporter identity comes from the session instead.
 * The requiredness rides on the field schemas rather than a whole-object refine,
 * so these errors surface in the same pass as the rest of the form.
 */
const reporterNameSchema = (t: TFunction, isAnonymous: boolean) =>
  z
    .string()
    .trim()
    .max(SUPPORT_REQUEST_NAME_MAX_LENGTH)
    .refine((name) => !isAnonymous || !!name, {
      error: t(RESOURCES.NameRequired),
    });

const reporterEmailSchema = (t: TFunction, isAnonymous: boolean) =>
  z
    .string()
    .trim()
    .refine((email) => !isAnonymous || !!email, {
      error: t(RESOURCES.EmailRequired),
    })
    .refine(
      (email) => !isAnonymous || !email || z.email().safeParse(email).success,
      {
        error: t(RESOURCES.EmailInvalid),
      },
    );

export const getSupportRequestSchema = (
  t: TFunction,
  isAnonymous: boolean,
  role: SupportRequestRole,
) =>
  z.object({
    // Narrowed to the role's own categories with a refine rather than a z.enum over a filtered
    // tuple, which would need a cast to satisfy Zod's non-empty-tuple type. Without this the
    // dropdown could be bypassed and the server would answer 400.
    category: z
      .enum(SupportRequestCategory, {
        error: t(RESOURCES.CategoryRequired),
      })
      .refine((category) => SUPPORT_ROLE_CATEGORIES[role].includes(category), {
        error: t(RESOURCES.CategoryRequired),
      }),
    subject: z
      .string()
      .trim()
      .min(1, { error: t(RESOURCES.SubjectRequired) })
      .max(SUPPORT_REQUEST_SUBJECT_MAX_LENGTH),
    description: z
      .string()
      .trim()
      .min(1, { error: t(RESOURCES.DescriptionRequired) })
      .min(SUPPORT_REQUEST_DESCRIPTION_MIN_LENGTH, {
        error: t(RESOURCES.DescriptionTooShort, {
          minLength: SUPPORT_REQUEST_DESCRIPTION_MIN_LENGTH,
        }),
      })
      .max(SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH),
    attachment: attachmentSchema(t),
    name: reporterNameSchema(t, isAnonymous),
    email: reporterEmailSchema(t, isAnonymous),
  });

export type SupportRequestFormData = z.infer<
  ReturnType<typeof getSupportRequestSchema>
>;
